import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./styles/UserStories.css";

import {
  Accordion,
  AccordionDetails,
  AccordionGroup,
  AccordionSummary,
  Button,
  Typography,
  Stack,
} from "@mui/joy";

import { api } from "../../../../interceptors/api";
import { useLayout } from "../../../LayoutContext";
import { DeviceUserStoriesDto } from "../../dto/DeviceUserStoriesDto";
import { getExhibitionConfig } from "../../config/exhibitionConfig";

interface StoryExplorationProps {
  exhibitionId: number;
}

const StoryExploration = ({ exhibitionId }: StoryExplorationProps) => {
  const [groupedStories, setGroupedStories] = useState<DeviceUserStoriesDto[]>(
    [],
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [lang, setLang] = useState<"DE" | "EN">("DE");
  const [openDeviceId, setOpenDeviceId] = useState<number | null>(null);
  const navigate = useNavigate();
  const { setExhibitionMode } = useLayout();

  const exhibitionConfig = getExhibitionConfig(exhibitionId);

  useEffect(() => {
    setExhibitionMode(true);
    return () => {
      setExhibitionMode(false);
    };
  }, [setExhibitionMode]);

  useEffect(() => {
    setLoading(true);
    api
      .get<DeviceUserStoriesDto[]>(
        `/api/user-story/all/exhibition/${exhibitionId}`,
      )
      .then((res) => {
        let stories = res.data;

        // If exhibition has a fixed device, filter to show only that device's stories
        // This could be removed if stories should live in multiple exhibitions
        if (exhibitionConfig?.fixedDeviceId) {
          stories = stories.filter(
            (item) => item.device.id === exhibitionConfig.fixedDeviceId,
          );
          // Auto-expand the accordion if there's only one device
          if (stories.length === 1) {
            setOpenDeviceId(stories[0].device.id);
          }
        }

        setGroupedStories(stories);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading stories", err);
        setError("Failed to load user stories.");
        toast.error("Failed to load user stories.");
        setLoading(false);
      });
  }, [exhibitionId, exhibitionConfig]);

  const handleAccordionChange = (deviceId: number) => {
    setOpenDeviceId((prev) => (prev === deviceId ? null : deviceId));
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <Typography>Loading stories, be patient dear visitor…</Typography>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <Typography color="danger">{error}</Typography>
      </div>
    );
  }

  return (
    <div className="story-sharing-container">
      <ToastContainer position="bottom-center" autoClose={3000} />

      <h1>User Stories</h1>

      <Stack direction="row" spacing={1} justifyContent="center" sx={{ mb: 3 }}>
        <Button
          variant={lang === "DE" ? "solid" : "outlined"}
          color="primary"
          onClick={() => setLang("DE")}
        >
          Deutsch
        </Button>
        <Button
          variant={lang === "EN" ? "solid" : "outlined"}
          color="primary"
          onClick={() => setLang("EN")}
        >
          English
        </Button>
      </Stack>

      <AccordionGroup size="md">
        {groupedStories.length === 0 ? (
          <p>No stories available.</p>
        ) : (
          groupedStories.map(({ device, userStories }) => (
            <Accordion
              key={device.id}
              expanded={openDeviceId === device.id}
              onChange={() => handleAccordionChange(device.id)}
            >
              <AccordionSummary>
                <h2>{device.name}</h2>
              </AccordionSummary>
              <AccordionDetails>
                {userStories.length === 0 ? (
                  <p>No stories for this device.</p>
                ) : (
                  userStories.map((story) => (
                    <div key={story.id}>
                      <h3>{lang === "DE" ? story.titleDE : story.titleEN}</h3>
                      <p>{lang === "DE" ? story.textDE : story.textEN}</p>
                    </div>
                  ))
                )}
              </AccordionDetails>
            </Accordion>
          ))
        )}
      </AccordionGroup>
      <Stack justifyContent="center" sx={{ mt: 4 }}>
        <Button
          variant="solid"
          color="neutral"
          onClick={() => {
            // Navigate to the appropriate share-story route based on exhibition
            if (exhibitionId === 4) {
              navigate("/exhibits/imac/share-story");
            } else {
              navigate("/exhibits/werkschau/share-story");
            }
          }}
        >
          Share Your Story
        </Button>
      </Stack>
    </div>
  );
};

export default StoryExploration;
